import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Output, inject, input, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Params, RouterLink } from '@angular/router';
import { MeasuredDataDetailsWaveService } from '../../../measured-data-details-wave.service';

@Component({
  selector: 'app-details-header',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, MatButtonModule, MatTooltipModule, RouterLink],
  template: `
    <div class="header">
      <div class="header__left">
        <span class="header__title">{{ title() }}</span>
        @if (badgeText()) {
          <span class="header__badge" [style.background-color]="badgeColor() + '15'" [style.color]="badgeColor()">
            {{ badgeText() }}
          </span>
        }
      </div>
      <div class="header__right">
        @if (patientName()) {
          <span class="header__patient">{{ patientName() }}</span>
        }
        @if (measureDate()) {
          <span class="header__meta">{{ measureDate() }}</span>
        }
        @if (measurer()) {
          <span class="header__meta">{{ measurer() }}</span>
        }
        @if (reportLink()) {
          <a
            mat-stroked-button
            class="header__report-link"
            [routerLink]="reportLink()"
            [queryParams]="reportQueryParams()"
            [attr.aria-label]="reportLinkLabel()"
            [matTooltip]="reportTooltip() || reportLinkLabel()"
            matTooltipClass="tooltip-wrap">
            <mat-icon fontSet="material-icons-outlined" class="header__report-link-icon">description</mat-icon>
            <span class="header__report-link-text">{{ reportLinkLabel() }}</span>
          </a>
        }
        <button
          mat-icon-button
          class="header__pdf"
          aria-label="PDF出力"
          i18n-aria-label="@@details.header.ariaLabel.pdfExport"
          [disabled]="isExporting()"
          (click)="onPdfClick()">
          <mat-icon>picture_as_pdf</mat-icon>
        </button>
      </div>
    </div>
  `,
  styles: [`
    .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--space-sm);
      padding: 0 var(--space-xl);
      height: 56px;
      background: var(--gokuri-surface);
    }
    .header__left {
      display: flex;
      align-items: center;
      gap: var(--space-md);
      min-width: 0;
    }
    .header__title {
      font-size: var(--font-size-title-sm);
      font-weight: 700;
      color: var(--gokuri-text-primary);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .header__badge {
      font-size: var(--font-size-label-sm);
      font-weight: 600;
      padding: var(--space-xs) var(--space-sm);
      border-radius: var(--radius-full);
      white-space: nowrap;
    }
    .header__right {
      display: flex;
      align-items: center;
      gap: var(--space-sm);
      min-width: 0;
      flex-wrap: nowrap;
      justify-content: flex-end;
    }
    .header__patient {
      font-size: var(--font-size-body-md);
      color: var(--gokuri-text-primary);
      white-space: nowrap;
    }
    .header__meta {
      font-size: var(--font-size-body-sm);
      color: var(--gokuri-text-secondary);
      white-space: nowrap;
    }
    .header__pdf {
      color: var(--gokuri-text-secondary);
    }
    .header__report-link {
      height: 26px;
      min-height: 26px;
      min-width: 0;
      border-color: var(--gokuri-border-light);
      background: var(--gokuri-surface);
      color: var(--gokuri-text-secondary);
      line-height: 1;
      padding: 0 11px;
      border-radius: 999px;
      text-decoration: none;
      transition: border-color 140ms ease, background-color 140ms ease;
    }
    .header__report-link:hover {
      border-color: var(--gokuri-border);
      background: var(--gokuri-surface-variant);
    }
    .header__report-link:focus-visible {
      outline: 2px solid var(--gokuri-border);
      outline-offset: 2px;
    }
    .header__report-link-text {
      font-size: var(--font-size-label-sm);
      font-weight: 600;
      letter-spacing: 0;
    }
    .header__report-link .header__report-link-icon {
      --header-report-link-icon-size: calc(var(--font-size-label-sm) * 1.2);
      width: var(--header-report-link-icon-size) !important;
      height: var(--header-report-link-icon-size) !important;
      line-height: var(--header-report-link-icon-size) !important;
      font-size: var(--header-report-link-icon-size) !important;
      margin-right: var(--space-2xs);
      color: var(--gokuri-text-secondary);
      flex: 0 0 auto;
    }

    @media (max-width: 767px) {
      .header {
        flex-wrap: wrap;
        align-items: flex-start;
        gap: var(--space-xs);
        padding: var(--space-xs) var(--space-md);
        height: auto;
      }
      .header__left {
        width: 100%;
        gap: var(--space-sm);
      }
      .header__title {
        font-size: var(--font-size-body-md);
      }
      .header__badge {
        font-size: var(--font-size-label-sm);
        padding: 2px 8px;
      }
      .header__right {
        width: 100%;
        justify-content: flex-start;
        flex-wrap: wrap;
        gap: 6px;
      }
      .header__patient {
        font-size: var(--font-size-body-sm);
      }
      .header__meta {
        font-size: var(--font-size-label-sm);
      }
      .header__pdf {
        margin-left: auto;
        width: 30px;
        height: 30px;
      }
      .header__report-link {
        height: 24px;
        min-height: 24px;
        padding: 0 9px;
      }
    }
  `],
})
export class DetailsHeaderComponent {
  private static readonly PDF_STAGE_MIN_WIDTH = 1180;
  private static readonly PDF_LAYOUT_WIDTH_PX = 900;
  private static readonly PDF_MARGIN_MM = 6;
  private static readonly PRINT_MODE_CLASS = 'details-pdf-print-mode';
  private static readonly PRINT_STYLE_ID = 'details-pdf-print-style';
  private readonly waveService = inject(MeasuredDataDetailsWaveService);
  private readonly hostElementRef = inject(ElementRef<HTMLElement>);

  readonly title = input.required<string>();
  readonly badgeText = input<string>();
  readonly badgeColor = input<string>('#E65100');
  readonly patientName = input<string>('');
  readonly measureDate = input<string>('');
  readonly measurer = input<string>('');
  readonly reportLink = input<string>('');
  readonly reportQueryParams = input<Params | null>(null);
  readonly reportLinkLabel = input<string>($localize`:@@details.header.reportLinkLabel:レポート`);
  readonly reportTooltip = input<string>('');
  readonly useCustomPdfHandler = input<boolean>(false);
  readonly isExporting = signal(false);
  @Output() readonly pdfClicked = new EventEmitter<void>();

  async onPdfClick(): Promise<void> {
    if (this.useCustomPdfHandler()) {
      this.pdfClicked.emit();
      return;
    }
    if (this.isExporting()) return;
    this.isExporting.set(true);
    try {
      await this.exportDetailsPdf();
    } catch (error) {
      console.error('[DetailsHeaderComponent] PDF export failed', error);
    } finally {
      this.isExporting.set(false);
    }
  }

  private async exportDetailsPdf(): Promise<void> {
    await this.waitForNextPaint();
    await this.waitForFontsReady();
    const scope = this.resolveCaptureScope(this.hostElementRef.nativeElement) ?? document.body;
    const restoreExpandedState = this.expandCollapsibleSections(scope);

    let stageRoot: HTMLDivElement | null = null;
    let cleanupPrintStyles: (() => void) | null = null;
    try {
      await this.waitForNextPaint();
      await this.waitForNextPaint();
      await this.waitForExpandedVisuals(scope);
      const targets = this.collectTargets();
      if (!targets.length) return;

      stageRoot = this.createCaptureStage(targets);
      this.configurePrintLayout(stageRoot);
      cleanupPrintStyles = this.installPrintStyles();
      document.body.classList.add(DetailsHeaderComponent.PRINT_MODE_CLASS);
      await this.waitForNextPaint();
      await this.waitForNextPaint();
      await this.triggerBrowserPrint();
    } finally {
      document.body.classList.remove(DetailsHeaderComponent.PRINT_MODE_CLASS);
      cleanupPrintStyles?.();
      stageRoot?.remove();
      restoreExpandedState();
    }
  }

  private collectTargets(): HTMLElement[] {
    const host = this.hostElementRef.nativeElement;
    const scope = this.resolveCaptureScope(host);
    const localRoots: ParentNode[] = scope ? [scope] : [document];
    const crossRoots: ParentNode[] = scope ? [scope, document] : [document];
    const contentTargets: HTMLElement[] = [];

    const cardWideTargets = this.findVisibleAll('.card-wide', localRoots)
      .sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top);
    contentTargets.push(...cardWideTargets);

    if (!cardWideTargets.length) {
      const gostLayoutTargets = this.findVisibleAll(
        '.gost-input-layout, .gost-summary-digest, .gost-digest-slot, .gost-timeline-card',
        localRoots,
      );
      if (gostLayoutTargets.length) {
        contentTargets.push(...gostLayoutTargets);
      }

      const gostDigestTargets = this.findVisibleAll(
        '.gost-digest-surface, .gost-digest-content, .gost-result-overview__detail-grid, .gost-digest-accordion',
        localRoots,
      );
      if (gostDigestTargets.length) {
        contentTargets.push(...gostDigestTargets);
      }

      if (!contentTargets.length) {
        const fallbackTargets = this.findVisibleAll('.details-content', localRoots);
        if (fallbackTargets.length) {
          contentTargets.push(...fallbackTargets);
        }
      }
    }

    if (!contentTargets.length && scope && this.isVisible(scope)) {
      contentTargets.push(scope);
    }

    const targets: HTMLElement[] = [...contentTargets];
    const hostAlreadyIncluded = contentTargets.some((target) => target.contains(host));
    if (!hostAlreadyIncluded && this.isVisible(host)) {
      targets.unshift(host);
    }

    const patientInfo = this.findFirstVisible(
      ['.patient-header__top-row', '.patient-header__info', '.m3-header__content'],
      crossRoots,
    );
    if (patientInfo) {
      const patientInfoAlreadyIncluded = targets.some((target) => target.contains(patientInfo));
      if (!patientInfoAlreadyIncluded) {
        targets.unshift(patientInfo);
      }
    }

    return this.normalizeTargets(targets);
  }

  private resolveCaptureScope(host: HTMLElement): HTMLElement | null {
    let current: HTMLElement | null = host.parentElement;
    while (current && current !== document.body) {
      if (
        current.querySelector('.card-wide')
        || current.querySelector('.gost-input-layout')
        || current.querySelector('.details-content')
        || current.querySelector('.gost-summary-digest')
        || current.querySelector('.gost-digest-slot')
        || current.querySelector('.gost-digest-surface')
        || current.querySelector('.gost-timeline-card')
      ) {
        return current;
      }
      current = current.parentElement;
    }
    return null;
  }

  private findFirstVisible(selectors: string[], roots: ParentNode[]): HTMLElement | null {
    for (const selector of selectors) {
      for (const root of roots) {
        const candidate = root.querySelector<HTMLElement>(selector);
        if (candidate && this.isVisible(candidate)) {
          return candidate;
        }
      }
    }
    return null;
  }

  private findVisibleAll(selector: string, roots: ParentNode[]): HTMLElement[] {
    const candidates: HTMLElement[] = [];
    for (const root of roots) {
      const nodes = Array.from(root.querySelectorAll<HTMLElement>(selector));
      for (const node of nodes) {
        if (this.isVisible(node)) {
          candidates.push(node);
        }
      }
    }
    return this.uniqueElements(candidates);
  }

  private uniqueElements(elements: HTMLElement[]): HTMLElement[] {
    return Array.from(new Set(elements));
  }

  private normalizeTargets(elements: HTMLElement[]): HTMLElement[] {
    const unique = this.uniqueElements(elements)
      .filter((element) => this.isVisible(element))
      .sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top);

    return unique.filter((element) => !unique.some((other) => other !== element && other.contains(element)));
  }

  private isVisible(element: HTMLElement): boolean {
    if (!element.isConnected) return false;
    const rect = element.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return false;

    let current: HTMLElement | null = element;
    while (current) {
      const style = window.getComputedStyle(current);
      if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) {
        return false;
      }
      current = current.parentElement;
    }

    return true;
  }

  private buildFileName(): string {
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, '0');
    const d = String(today.getDate()).padStart(2, '0');
    return `detail_${y}${m}${d}.pdf`;
  }

  private async waitForNextPaint(): Promise<void> {
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
  }

  private async waitForFontsReady(): Promise<void> {
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    if (!fonts?.ready) return;
    await Promise.race([
      fonts.ready.then(() => undefined),
      new Promise<void>((resolve) => setTimeout(resolve, 1200)),
    ]);
  }

  private installPrintStyles(): () => void {
    const existing = document.getElementById(DetailsHeaderComponent.PRINT_STYLE_ID);
    if (existing) existing.remove();

    const styleTag = document.createElement('style');
    styleTag.id = DetailsHeaderComponent.PRINT_STYLE_ID;
    styleTag.textContent = `
      @media screen {
        .details-pdf-stage-root {
          display: none !important;
        }
      }

      @media print {
        @page {
          size: A4 portrait;
          margin: 0;
        }

        html,
        body {
          margin: 0 !important;
          padding: 0 !important;
          background: #fff !important;
        }

        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} > * {
          display: none !important;
        }

        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} > .details-pdf-stage-root {
          display: flex !important;
          position: static !important;
          left: 0 !important;
          top: 0 !important;
          width: var(--details-pdf-layout-width, 100%) !important;
          max-width: none !important;
          padding: ${DetailsHeaderComponent.PDF_MARGIN_MM}mm !important;
          box-sizing: border-box !important;
          margin: 0 !important;
          background: #fff !important;
          gap: 10px !important;
          z-index: auto !important;
          pointer-events: none !important;
          opacity: 1 !important;
          transform-origin: top left !important;
          zoom: var(--details-pdf-layout-scale, 1) !important;
        }

        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root > section {
          break-inside: auto;
          page-break-inside: auto;
          width: var(--details-pdf-layout-width, 100%) !important;
        }

        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root,
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root * {
          animation: none !important;
          transition: none !important;
          text-shadow: none !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .header {
          height: auto !important;
          min-height: 56px !important;
        }

        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .header__left,
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .header__right {
          min-width: 0 !important;
        }

        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .header__right {
          flex-wrap: nowrap !important;
          flex-shrink: 0 !important;
        }

        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .header__title {
          white-space: nowrap !important;
          overflow: visible !important;
          text-overflow: clip !important;
          word-break: keep-all !important;
          overflow-wrap: normal !important;
        }

        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .header__pdf {
          display: none !important;
        }

        /* パンくずリスト: PDF では back ボタン・戻りラベル・区切りアイコンを非表示 */
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .patient-header__back-btn,
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .patient-header__back-label,
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .patient-header__crumb-sep {
          display: none !important;
        }

        /* 戻りラベルを消した分、名前左の区切り縦線も非表示にする */
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .patient-header__info {
          border-left: none !important;
          margin-left: 0 !important;
          padding-left: 0 !important;
        }

        /* GOSTレポート: 手動入力ボタン行を印刷対象から除外 */
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .header-container {
          display: none !important;
        }

        /* GOST詳細: 波形・姿勢アコーディオンはPDF対象外 */
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .gost-timeline-card {
          display: none !important;
        }

        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .timeline-card__title,
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .gost-timeline-card__title,
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .attempt-card__title,
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .evt-header__title,
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .evt-header__count,
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .evt-header__hint,
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .ref-pill {
          word-break: keep-all !important;
          overflow-wrap: normal !important;
        }

        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .ref-card__pills {
          flex-wrap: wrap !important;
        }

        /* Keep 2-column sections in print (A4 effective width ~748px) */
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .gost-result-overview__detail-grid {
          grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important;
        }

        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .gost-digest-content {
          grid-template-columns: minmax(200px, 1fr) minmax(0, 2.5fr) !important;
          gap: var(--space-lg) !important;
          align-items: start !important;
        }

        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .gost-digest-content .left-panel {
          position: static !important;
          top: auto !important;
          width: auto !important;
        }

        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .contents,
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .contents_section_group,
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .contents__section,
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .main_body,
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .gost-digest-slot {
          flex: 0 0 auto !important;
          min-height: 0 !important;
          height: auto !important;
        }

        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .gost-input-questionnaire {
          grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          gap: var(--space-sm) !important;
        }

        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .gost-input-questionnaire__summary {
          grid-column: 1 / -1 !important;
        }

        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .protocol-mwst .kpi-row,
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .protocol-rsst .kpi-row,
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .protocol-sstp .kpi-row,
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .protocol-tomass .kpi-row {
          flex-direction: row !important;
          gap: var(--space-md) !important;
          align-items: stretch !important;
        }

        /* TOMASS: detail grid stays multi-column in print */
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .protocol-tomass .detail-grid {
          grid-template-columns: 1fr 1fr 1fr !important;
          gap: var(--space-md) var(--space-xl) !important;
        }

        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .protocol-tomass .detail-grid__item--full {
          grid-column: 1 / -1 !important;
        }

        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .protocol-mwst .kpi-row--thirds {
          display: grid !important;
          grid-template-columns: 1fr 1fr 2fr !important;
          gap: var(--space-md) !important;
        }

        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .protocol-rsst .kpi-row--triple {
          display: grid !important;
          grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
          gap: var(--space-md) !important;
        }

        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .protocol-rsst .kpi-row--triple > :last-child {
          grid-column: auto !important;
        }

        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .protocol-monitoring .pictures-card__grid {
          grid-template-columns: 1fr 1fr !important;
        }

        /* Keep desktop-like two-column layout for GOST sections in print */
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .gost-sc-workspace {
          grid-template-columns: minmax(220px, 280px) minmax(0, 1fr) !important;
        }

        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .gost-sc-desktop {
          grid-template-columns: minmax(191px, 1fr) minmax(340px, 2.2fr) !important;
        }

        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .gost-sc-trials {
          max-height: none !important;
          overflow: visible !important;
        }

        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .gost-digest-accordion .gost_recording_item_set {
          grid-template-columns: minmax(220px, 0.95fr) minmax(0, 1.65fr) !important;
          align-items: center !important;
          column-gap: var(--space-lg, 16px) !important;
        }

        /* Print timeline: force 2 columns without overlap */
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .timeline-card__grid,
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .gost-timeline-card__grid {
          display: grid !important;
          grid-template-columns: minmax(0, 1fr) 228px !important;
          column-gap: 14px !important;
          row-gap: 0 !important;
          grid-auto-flow: row !important;
          align-items: stretch !important;
        }

        /* Reset mobile ordering so explicit grid-row/grid-column take effect */
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .timeline-card__grid > *,
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .gost-timeline-card__grid > * {
          order: unset !important;
        }

        /* ── Default (no video): wave + posture side-by-side ── */
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .timeline-card__wave,
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .gost-timeline-card__wave {
          grid-column: 1 !important;
          grid-row: 1 !important;
          min-width: 0 !important;
          max-width: 100% !important;
          overflow: hidden !important;
          position: relative !important;
          z-index: 0 !important;
          box-sizing: border-box !important;
        }

        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .timeline-card__posture,
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .gost-timeline-card__posture {
          grid-column: 2 !important;
          grid-row: 1 !important;
          min-width: 228px !important;
          max-width: 228px !important;
          overflow: hidden !important;
          position: relative !important;
          z-index: 1 !important;
          background: #fff !important;
          --rsst-posture-min-height: 212px !important;
          --rsst-posture-max-height: 212px !important;
        }

        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .timeline-card__events,
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .timeline-card__footer,
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .gost-timeline-card__events,
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .gost-timeline-card__footer {
          grid-column: 1 / -1 !important;
        }

        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .timeline-card__events,
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .gost-timeline-card__events {
          grid-row: 2 !important;
        }

        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .timeline-card__footer,
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .gost-timeline-card__footer {
          grid-row: 3 !important;
        }

        /* ── VS: video present → video+posture row1, wave full-width row2 ── */
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .timeline-card__video {
          grid-column: 1 !important;
          grid-row: 1 !important;
          min-width: 0 !important;
          max-width: 100% !important;
          overflow: hidden !important;
          order: unset !important;
        }

        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .timeline-card__grid:has(.timeline-card__video) .timeline-card__wave {
          grid-column: 1 / -1 !important;
          grid-row: 2 !important;
        }

        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .timeline-card__grid:has(.timeline-card__video) .timeline-card__events {
          grid-row: 3 !important;
        }

        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .timeline-card__grid:has(.timeline-card__video) .timeline-card__footer {
          grid-row: 4 !important;
        }

        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .timeline-card__wave app-measured-data-wave-card,
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .gost-timeline-card__wave app-measured-data-wave-card {
          display: block !important;
          width: 100% !important;
          max-width: 100% !important;
          min-width: 0 !important;
        }

        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .timeline-card__wave .wave-div,
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .gost-timeline-card__wave .wave-div,
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .timeline-card__wave .wave-div [part="wave"],
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .timeline-card__wave .wave-div [part="regions"],
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .timeline-card__wave .wave-div [part~="region"],
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .gost-timeline-card__wave .wave-div [part="wave"],
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .gost-timeline-card__wave .wave-div [part="regions"],
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .gost-timeline-card__wave .wave-div [part~="region"] {
          max-width: 100% !important;
          overflow: hidden !important;
        }

        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .timeline-card__posture app-measured-data-neck-angle-card,
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .gost-timeline-card__posture app-measured-data-neck-angle-card {
          display: block !important;
          width: 100% !important;
          min-height: 212px !important;
        }

        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .timeline-card__posture app-measured-data-neck-angle-card .mat-card-angle .mat-mdc-card-content,
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .gost-timeline-card__posture app-measured-data-neck-angle-card .mat-card-angle .mat-mdc-card-content {
          --neck-angle-hint-space: calc(var(--font-size-body-sm) * 1.6 + var(--space-xs)) !important;
          min-height: 212px !important;
          max-height: none !important;
        }

        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .timeline-card__posture app-measured-data-neck-angle-card .scatter-layer,
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .gost-timeline-card__posture app-measured-data-neck-angle-card .scatter-layer {
          bottom: calc(var(--space-xs) + var(--neck-angle-hint-space) + 2px) !important;
        }

        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .timeline-card__posture app-measured-data-neck-angle-card .angle-hint,
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .gost-timeline-card__posture app-measured-data-neck-angle-card .angle-hint {
          line-height: 1.2 !important;
        }

        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .evt-table__body {
          max-height: none !important;
          overflow: visible !important;
        }

        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .attempt-card__table-wrap,
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .timeline-card__events,
        body.${DetailsHeaderComponent.PRINT_MODE_CLASS} .details-pdf-stage-root .gost-timeline-card__events {
          overflow: visible !important;
          max-height: none !important;
        }
      }
    `;
    document.head.appendChild(styleTag);
    return () => {
      styleTag.remove();
    };
  }

  private async triggerBrowserPrint(): Promise<void> {
    const previousTitle = document.title;
    document.title = this.buildFileName().replace(/\.pdf$/i, '');

    await new Promise<void>((resolve) => {
      let settled = false;
      const complete = () => {
        if (settled) return;
        settled = true;
        window.removeEventListener('afterprint', onAfterPrint);
        resolve();
      };
      const onAfterPrint = () => complete();
      window.addEventListener('afterprint', onAfterPrint, { once: true });

      setTimeout(() => {
        try {
          window.print();
        } catch {
          complete();
        }
      }, 50);

      setTimeout(complete, 45_000);
    });

    document.title = previousTitle;
  }

  private expandCollapsibleSections(scope: ParentNode): () => void {
    const selectors = [
      '.mat-expansion-panel-header[aria-expanded="false"]',
      '.attempt-card__header[aria-expanded="false"]',
      '.detail-card__header[aria-expanded="false"]',
      // NOTE: .gost-timeline-card is hidden via print CSS (display:none),
      // so we skip expanding it to avoid a 20s timeout in waitForExpandedVisuals.
    ];
    const toggled: HTMLElement[] = [];
    const toggles = Array.from(scope.querySelectorAll<HTMLElement>(selectors.join(',')));

    for (const toggle of toggles) {
      if (!this.isVisible(toggle) || !this.isCollapsedToggle(toggle)) continue;
      toggle.click();
      toggled.push(toggle);
    }

    return () => {
      for (let i = toggled.length - 1; i >= 0; i -= 1) {
        const toggle = toggled[i];
        if (!toggle.isConnected || !this.isVisible(toggle)) continue;
        if (!this.isCollapsedToggle(toggle)) {
          toggle.click();
        }
      }
    };
  }

  private isCollapsedToggle(toggle: HTMLElement): boolean {
    const ariaExpanded = toggle.getAttribute('aria-expanded');
    if (ariaExpanded === 'false') {
      return true;
    }
    if (toggle.classList.contains('mat-expansion-panel-header')) {
      return !toggle.classList.contains('mat-expanded');
    }
    return false;
  }

  private configurePrintLayout(stageRoot: HTMLDivElement): void {
    const a4WidthPx = (210 / 25.4) * 96;
    const marginPx = (DetailsHeaderComponent.PDF_MARGIN_MM / 25.4) * 96;
    const printableWidthPx = Math.max(1, a4WidthPx - marginPx * 2);
    const layoutWidthPx = Math.max(printableWidthPx, DetailsHeaderComponent.PDF_LAYOUT_WIDTH_PX);
    const layoutScale = Math.min(1, printableWidthPx / layoutWidthPx);

    stageRoot.style.setProperty('--details-pdf-layout-width', `${layoutWidthPx}px`);
    stageRoot.style.setProperty('--details-pdf-layout-scale', layoutScale.toFixed(4));
  }

  private async waitForExpandedVisuals(scope: ParentNode, timeoutMs = 20000): Promise<boolean> {
    const startedAt = Date.now();

    while (Date.now() - startedAt < timeoutMs) {
      if (this.areExpandedVisualsReady(scope)) {
        return true;
      }
      await new Promise<void>((resolve) => setTimeout(resolve, 120));
    }
    return false;
  }

  private areExpandedVisualsReady(scope: ParentNode): boolean {
    const waveHosts = Array.from(scope.querySelectorAll<HTMLElement>('.wave-div'))
      .filter((host) => this.isVisible(host));
    // GOST timeline card is hidden in print CSS, so we don't check it here.
    const waveLoadingVisible = Array.from(scope.querySelectorAll<HTMLElement>('.wave-loading-overlay'))
      .some((overlay) => this.isVisible(overlay));
    if (waveLoadingVisible) {
      return false;
    }
    const waveReady = waveHosts.length === 0 || waveHosts.every((host) => this.hasRenderableWaveCanvas(host));

    const postureContainers = Array.from(
      scope.querySelectorAll<HTMLElement>('.timeline-card__posture, .gost-timeline-card__posture'),
    ).filter((container) => this.isVisible(container));
    const postureReady = postureContainers.length === 0 || postureContainers.every((container) => {
      const chartCanvas = container.querySelector<HTMLCanvasElement>('canvas');
      if (chartCanvas && chartCanvas.width > 0 && chartCanvas.height > 0) {
        return true;
      }
      return !!container.querySelector<SVGElement>('svg');
    });

    return waveReady && postureReady;
  }

  private hasRenderableWaveCanvas(sourceHost: HTMLElement): boolean {
    const shadowCanvases = this.collectShadowCanvases(sourceHost);
    if (this.hasMeaningfulWaveInk(shadowCanvases)) {
      return true;
    }

    const waveSurfer = this.waveService.getWaveSurfer() as { getWrapper?: () => HTMLElement } | undefined;
    const wrapper = waveSurfer?.getWrapper?.();
    if (!wrapper) return false;
    if (!this.isWaveSurferWrapperWithinHost(wrapper, sourceHost)) {
      return false;
    }

    const wrapperCanvases = Array.from(wrapper.querySelectorAll<HTMLCanvasElement>('canvas'));
    return this.hasMeaningfulWaveInk(wrapperCanvases);
  }

  private hasMeaningfulWaveInk(canvases: HTMLCanvasElement[], minInkScore = 3): boolean {
    return canvases.some(
      (canvas) => canvas.width > 0 && canvas.height > 0 && this.getCanvasInkScore(canvas) > minInkScore,
    );
  }

  private createCaptureStage(targets: HTMLElement[]): HTMLDivElement {
    const stageRoot = document.createElement('div');
    stageRoot.className = 'details-pdf-stage-root';
    const stageWidth = Math.max(
      DetailsHeaderComponent.PDF_STAGE_MIN_WIDTH,
      window.innerWidth,
      ...targets.map((target) => Math.ceil(Math.max(
        target.getBoundingClientRect().width,
        target.scrollWidth,
      ))),
    );

    stageRoot.style.position = 'fixed';
    stageRoot.style.left = '-20000px';
    stageRoot.style.top = '0';
    stageRoot.style.width = `${stageWidth}px`;
    stageRoot.style.maxWidth = 'none';
    stageRoot.style.background = '#ffffff';
    stageRoot.style.padding = '16px';
    stageRoot.style.display = 'flex';
    stageRoot.style.flexDirection = 'column';
    stageRoot.style.gap = '16px';
    stageRoot.style.zIndex = '-1';
    stageRoot.style.pointerEvents = 'none';
    stageRoot.style.opacity = '1';

    for (const target of targets) {
      const block = document.createElement('section');
      block.style.width = '100%';
      block.style.background = '#ffffff';

      const clone = target.cloneNode(true) as HTMLElement;
      const rect = target.getBoundingClientRect();
      const stretchToStage =
        target.classList.contains('card-wide')
        || target.classList.contains('details-content')
        || target.classList.contains('gost-input-layout')
        || target.classList.contains('gost-digest-slot')
        || target.classList.contains('gost-summary-digest')
        || target.classList.contains('gost-timeline-card');
      clone.style.width = stretchToStage
        ? '100%'
        : `${Math.ceil(Math.max(rect.width, target.scrollWidth))}px`;
      clone.style.maxWidth = '100%';
      clone.style.margin = '0';
      clone.style.contain = 'none';
      this.copyCanvasPixels(target, clone);
      this.copyShadowWaveCanvases(target, clone);

      block.appendChild(clone);
      stageRoot.appendChild(block);
    }

    document.body.appendChild(stageRoot);
    return stageRoot;
  }

  private copyCanvasPixels(sourceRoot: HTMLElement, cloneRoot: HTMLElement): void {
    const sourceCanvases = Array.from(sourceRoot.querySelectorAll('canvas'));
    const cloneCanvases = Array.from(cloneRoot.querySelectorAll('canvas'));
    const pairCount = Math.min(sourceCanvases.length, cloneCanvases.length);

    for (let i = 0; i < pairCount; i += 1) {
      const source = sourceCanvases[i];
      const clone = cloneCanvases[i];
      if (!source || !clone) continue;

      clone.width = source.width;
      clone.height = source.height;

      const context = clone.getContext('2d');
      if (!context) continue;

      try {
        context.drawImage(source, 0, 0);
      } catch (error) {
        console.warn('[DetailsHeaderComponent] canvas clone skipped', error);
      }
    }
  }

  private copyShadowWaveCanvases(sourceRoot: HTMLElement, cloneRoot: HTMLElement): void {
    const sourceHosts = [
      ...(sourceRoot.matches('.wave-div') ? [sourceRoot] : []),
      ...Array.from(sourceRoot.querySelectorAll<HTMLElement>('.wave-div')),
    ];
    const cloneHosts = [
      ...(cloneRoot.matches('.wave-div') ? [cloneRoot] : []),
      ...Array.from(cloneRoot.querySelectorAll<HTMLElement>('.wave-div')),
    ];
    const pairCount = Math.min(sourceHosts.length, cloneHosts.length);

    for (let i = 0; i < pairCount; i += 1) {
      const sourceHost = sourceHosts[i] as HTMLElement & { shadowRoot?: ShadowRoot | null };
      const cloneHost = cloneHosts[i];
      if (!sourceHost || !cloneHost) continue;
      const hostRect = sourceHost.getBoundingClientRect();
      if (hostRect.width <= 0 || hostRect.height <= 0) continue;

      let merged = this.buildMergedWaveCanvasFromShadowRoot(sourceHost, hostRect);
      if (!merged || !this.isCanvasMeaningful(merged)) {
        const mergedFromLightDom = this.buildMergedWaveCanvasFromLightDom(sourceHost, hostRect);
        if (mergedFromLightDom && this.isCanvasMeaningful(mergedFromLightDom)) {
          merged = mergedFromLightDom;
        }
      }
      if (!merged || !this.isCanvasMeaningful(merged)) {
        const mergedFromWaveService = this.buildMergedWaveCanvasFromWaveService(sourceHost, hostRect);
        if (mergedFromWaveService && this.isCanvasMeaningful(mergedFromWaveService)) {
          merged = mergedFromWaveService;
        }
      }
      if (!merged || !this.isCanvasMeaningful(merged)) {
        const mergedFromAnySource = this.buildMergedWaveCanvasFromAnySource(sourceHost, hostRect);
        if (mergedFromAnySource && this.isCanvasMeaningful(mergedFromAnySource)) {
          merged = mergedFromAnySource;
        }
      }
      if ((!merged || !this.isCanvasMeaningful(merged))
        && sourceHost.shadowRoot
        && this.cloneWaveShadowDom(sourceHost, cloneHost, hostRect, false)) {
        this.copyWaveMarkerOverlays(sourceHost, cloneHost, hostRect);
        continue;
      }
      if (!merged || !this.isCanvasMeaningful(merged)) continue;

      this.mountWaveCanvas(cloneHost, hostRect, merged);
      this.copyWaveMarkerOverlays(sourceHost, cloneHost, hostRect);
    }
  }

  private cloneWaveShadowDom(
    sourceHost: HTMLElement & { shadowRoot?: ShadowRoot | null },
    cloneHost: HTMLElement,
    hostRect: DOMRect,
    requireMeaningfulInk = true,
  ): boolean {
    const sourceShadow = sourceHost.shadowRoot;
    if (!sourceShadow) return false;
    const sourceShadowCanvases = Array.from(sourceShadow.querySelectorAll<HTMLCanvasElement>('canvas'));
    if (!sourceShadowCanvases.length) return false;
    if (requireMeaningfulInk && !sourceShadowCanvases.some((canvas) => this.getCanvasInkScore(canvas) > 3)) {
      return false;
    }

    const widthPx = `${Math.ceil(hostRect.width)}px`;
    const heightPx = `${Math.ceil(hostRect.height)}px`;
    cloneHost.innerHTML = '';
    cloneHost.style.position = 'relative';
    cloneHost.style.overflow = 'hidden';
    cloneHost.style.display = 'block';
    cloneHost.style.width = widthPx;
    cloneHost.style.height = heightPx;
    cloneHost.style.minHeight = heightPx;

    const targetShadow = cloneHost.shadowRoot ?? cloneHost.attachShadow({ mode: 'open' });
    targetShadow.innerHTML = '';
    for (const child of Array.from(sourceShadow.childNodes)) {
      targetShadow.appendChild(child.cloneNode(true));
    }

    const cloneCanvases = Array.from(targetShadow.querySelectorAll<HTMLCanvasElement>('canvas'));
    const pairCount = Math.min(sourceShadowCanvases.length, cloneCanvases.length);
    let drawn = false;

    for (let i = 0; i < pairCount; i += 1) {
      const sourceCanvas = sourceShadowCanvases[i];
      const cloneCanvas = cloneCanvases[i];
      if (!sourceCanvas || !cloneCanvas) continue;

      cloneCanvas.width = sourceCanvas.width;
      cloneCanvas.height = sourceCanvas.height;
      const context = cloneCanvas.getContext('2d');
      if (!context) continue;

      try {
        context.drawImage(sourceCanvas, 0, 0);
        drawn = true;
      } catch (error) {
        console.warn('[DetailsHeaderComponent] shadow canvas copy skipped', error);
      }
    }

    return drawn;
  }

  private copyWaveMarkerOverlays(
    sourceHost: HTMLElement & { shadowRoot?: ShadowRoot | null },
    cloneHost: HTMLElement,
    hostRect: DOMRect,
  ): void {
    const markerSources = this.collectWaveMarkerElements(
      sourceHost,
      '[part~="region"], [part="region"], [data-wave-marker="true"], .wave-marker-tag, .wavesurfer-region',
    );
    const labelSources = this.collectWaveMarkerElements(
      sourceHost,
      '[data-wave-marker-label="true"], .wave-marker-label',
    );

    if (!markerSources.length && !labelSources.length) return;

    const overlay = document.createElement('div');
    overlay.style.position = 'absolute';
    overlay.style.inset = '0';
    overlay.style.pointerEvents = 'none';
    overlay.style.zIndex = '5';

    let hasOverlayElement = false;
    for (const marker of markerSources) {
      const clonedMarker = this.cloneWaveOverlayElement(marker, hostRect, false);
      if (!clonedMarker) continue;
      overlay.appendChild(clonedMarker);
      hasOverlayElement = true;
    }

    for (const label of labelSources) {
      const clonedLabel = this.cloneWaveOverlayElement(label, hostRect, true);
      if (!clonedLabel) continue;
      overlay.appendChild(clonedLabel);
      hasOverlayElement = true;
    }

    if (hasOverlayElement) {
      cloneHost.appendChild(overlay);
    }
  }

  private collectWaveMarkerElements(
    sourceHost: HTMLElement & { shadowRoot?: ShadowRoot | null },
    selector: string,
  ): HTMLElement[] {
    const sources: HTMLElement[] = [];
    for (const host of this.collectShadowHosts(sourceHost)) {
      const shadowRoot = host.shadowRoot;
      if (!shadowRoot) continue;
      sources.push(...Array.from(shadowRoot.querySelectorAll<HTMLElement>(selector)));
    }
    return this.uniqueElements(sources);
  }

  private collectShadowCanvases(
    sourceHost: HTMLElement & { shadowRoot?: ShadowRoot | null },
  ): HTMLCanvasElement[] {
    const canvases: HTMLCanvasElement[] = [];
    for (const host of this.collectShadowHosts(sourceHost)) {
      const shadowRoot = host.shadowRoot;
      if (!shadowRoot) continue;
      canvases.push(...Array.from(shadowRoot.querySelectorAll<HTMLCanvasElement>('canvas')));
    }
    return this.uniqueCanvasElements(canvases);
  }

  private collectShadowHosts(
    sourceHost: HTMLElement & { shadowRoot?: ShadowRoot | null },
  ): Array<HTMLElement & { shadowRoot?: ShadowRoot | null }> {
    return [
      sourceHost,
      ...Array.from(sourceHost.querySelectorAll<HTMLElement>('*')),
    ].filter((element): element is HTMLElement & { shadowRoot?: ShadowRoot | null } => !!element);
  }

  private cloneWaveOverlayElement(
    sourceElement: HTMLElement,
    hostRect: DOMRect,
    copyText: boolean,
  ): HTMLDivElement | null {
    const sourceRect = sourceElement.getBoundingClientRect();
    if (sourceRect.width <= 0 || sourceRect.height <= 0) return null;

    const style = window.getComputedStyle(sourceElement);
    const clone = document.createElement('div');
    clone.style.position = 'absolute';
    clone.style.left = `${sourceRect.left - hostRect.left}px`;
    clone.style.top = `${sourceRect.top - hostRect.top}px`;
    clone.style.width = `${sourceRect.width}px`;
    clone.style.height = `${sourceRect.height}px`;
    clone.style.boxSizing = 'border-box';
    clone.style.pointerEvents = 'none';
    clone.style.background = style.background;
    clone.style.backgroundColor = style.backgroundColor;
    clone.style.border = style.border;
    clone.style.borderRadius = style.borderRadius;
    clone.style.clipPath = style.clipPath === 'none' ? '' : style.clipPath;
    clone.style.opacity = style.opacity;
    clone.style.color = style.color;
    clone.style.fontSize = style.fontSize;
    clone.style.fontWeight = style.fontWeight;
    clone.style.fontFamily = style.fontFamily;
    clone.style.lineHeight = style.lineHeight;
    clone.style.whiteSpace = style.whiteSpace;
    clone.style.padding = style.padding;
    clone.style.display = style.display === 'none' ? 'block' : style.display;
    clone.style.alignItems = style.alignItems;
    clone.style.justifyContent = style.justifyContent;
    clone.style.textAlign = style.textAlign;

    if (copyText) {
      clone.textContent = sourceElement.textContent ?? '';
    }

    return clone;
  }

  private buildMergedWaveCanvasFromShadowRoot(
    sourceHost: HTMLElement & { shadowRoot?: ShadowRoot | null },
    hostRect: DOMRect,
  ): HTMLCanvasElement | null {
    return this.buildMergedWaveCanvas(this.collectShadowCanvases(sourceHost), hostRect);
  }

  private buildMergedWaveCanvasFromLightDom(
    sourceHost: HTMLElement,
    hostRect: DOMRect,
  ): HTMLCanvasElement | null {
    const canvases = Array.from(sourceHost.querySelectorAll<HTMLCanvasElement>('canvas'));
    return this.buildMergedWaveCanvas(this.uniqueCanvasElements(canvases), hostRect);
  }

  private buildMergedWaveCanvasFromWaveService(
    sourceHost: HTMLElement,
    hostRect: DOMRect,
  ): HTMLCanvasElement | null {
    const waveSurfer = this.waveService.getWaveSurfer() as { getWrapper?: () => HTMLElement } | undefined;
    const wrapper = waveSurfer?.getWrapper?.();
    if (!wrapper) return null;
    if (!this.isWaveSurferWrapperWithinHost(wrapper, sourceHost)) return null;

    const wrapperRect = wrapper.getBoundingClientRect();
    if (wrapperRect.width <= 0 || wrapperRect.height <= 0) return null;
    const sourceCanvases = Array.from(wrapper.querySelectorAll<HTMLCanvasElement>('canvas'));
    return this.buildMergedWaveCanvas(
      this.uniqueCanvasElements(sourceCanvases),
      hostRect,
      wrapperRect,
    );
  }

  private buildMergedWaveCanvasFromAnySource(
    sourceHost: HTMLElement,
    hostRect: DOMRect,
  ): HTMLCanvasElement | null {
    const canvases: HTMLCanvasElement[] = [];

    canvases.push(...Array.from(sourceHost.querySelectorAll<HTMLCanvasElement>('canvas')));
    canvases.push(
      ...this.collectShadowCanvases(sourceHost as HTMLElement & { shadowRoot?: ShadowRoot | null }),
    );

    const waveSurfer = this.waveService.getWaveSurfer() as { getWrapper?: () => HTMLElement } | undefined;
    const wrapper = waveSurfer?.getWrapper?.();
    if (wrapper && this.isWaveSurferWrapperWithinHost(wrapper, sourceHost)) {
      canvases.push(...Array.from(wrapper.querySelectorAll<HTMLCanvasElement>('canvas')));
    }

    return this.buildMergedWaveCanvas(this.uniqueCanvasElements(canvases), hostRect);
  }

  private uniqueCanvasElements(canvases: HTMLCanvasElement[]): HTMLCanvasElement[] {
    return Array.from(new Set(canvases));
  }

  private isWaveSurferWrapperWithinHost(wrapper: HTMLElement, sourceHost: HTMLElement): boolean {
    if (sourceHost.contains(wrapper)) {
      return true;
    }
    const root = wrapper.getRootNode();
    if (root instanceof ShadowRoot && root.host instanceof HTMLElement) {
      return sourceHost.contains(root.host);
    }
    return false;
  }

  private buildMergedWaveCanvas(
    sourceCanvases: HTMLCanvasElement[],
    outputRect: DOMRect,
    referenceRect: DOMRect = outputRect,
  ): HTMLCanvasElement | null {
    if (!sourceCanvases.length) return null;
    if (outputRect.width <= 0 || outputRect.height <= 0) return null;
    if (referenceRect.width <= 0 || referenceRect.height <= 0) return null;

    const rasterScale = Math.min(3, Math.max(1.5, window.devicePixelRatio || 1));
    const merged = document.createElement('canvas');
    merged.width = Math.max(1, Math.ceil(outputRect.width * rasterScale));
    merged.height = Math.max(1, Math.ceil(outputRect.height * rasterScale));
    const context = merged.getContext('2d');
    if (!context) return null;

    context.scale(rasterScale, rasterScale);
    context.clearRect(0, 0, outputRect.width, outputRect.height);
    const scaleX = outputRect.width / referenceRect.width;
    const scaleY = outputRect.height / referenceRect.height;

    let drawn = false;
    for (const sourceCanvas of sourceCanvases) {
      const canvasRect = sourceCanvas.getBoundingClientRect();
      const drawWidth = canvasRect.width;
      const drawHeight = canvasRect.height;
      if (drawWidth <= 0 || drawHeight <= 0) continue;

      const drawX = (canvasRect.left - referenceRect.left) * scaleX;
      const drawY = (canvasRect.top - referenceRect.top) * scaleY;
      const visibleRect = this.resolveVisibleRectForCanvas(sourceCanvas);
      if (!visibleRect || visibleRect.width <= 0 || visibleRect.height <= 0) continue;
      const clipX = (visibleRect.left - referenceRect.left) * scaleX;
      const clipY = (visibleRect.top - referenceRect.top) * scaleY;
      const clipWidth = visibleRect.width * scaleX;
      const clipHeight = visibleRect.height * scaleY;
      const scaledWidth = drawWidth * scaleX;
      const scaledHeight = drawHeight * scaleY;

      try {
        context.save();
        context.beginPath();
        context.rect(clipX, clipY, clipWidth, clipHeight);
        context.clip();
        context.drawImage(sourceCanvas, drawX, drawY, scaledWidth, scaledHeight);
        context.restore();
        drawn = true;
      } catch (error) {
        context.restore();
        console.warn('[DetailsHeaderComponent] shadow canvas clone skipped', error);
      }
    }

    return drawn ? merged : null;
  }

  private mountWaveCanvas(cloneHost: HTMLElement, hostRect: DOMRect, merged: HTMLCanvasElement): void {
    const widthPx = `${Math.ceil(hostRect.width)}px`;
    const heightPx = `${Math.ceil(hostRect.height)}px`;

    cloneHost.innerHTML = '';
    cloneHost.style.position = 'relative';
    cloneHost.style.overflow = 'hidden';
    cloneHost.style.display = 'block';
    cloneHost.style.width = widthPx;
    cloneHost.style.height = heightPx;
    cloneHost.style.minHeight = heightPx;

    merged.style.width = widthPx;
    merged.style.height = heightPx;
    merged.style.display = 'block';
    cloneHost.appendChild(merged);
  }

  private isCanvasMeaningful(canvas: HTMLCanvasElement): boolean {
    return this.getCanvasInkScore(canvas) > 8;
  }

  private resolveVisibleRectForCanvas(sourceCanvas: HTMLCanvasElement): DOMRect | null {
    let visibleRect = sourceCanvas.getBoundingClientRect();
    if (visibleRect.width <= 0 || visibleRect.height <= 0) {
      return null;
    }

    let current: HTMLElement | null = sourceCanvas.parentElement;
    let crossedShadowBoundary = false;

    while (current) {
      if (this.shouldClipByAncestor(current)) {
        const intersected = this.intersectRects(visibleRect, current.getBoundingClientRect());
        if (!intersected) return null;
        visibleRect = intersected;
      }

      current = current.parentElement;
      if (!current && !crossedShadowBoundary) {
        const rootNode = sourceCanvas.getRootNode();
        if (rootNode instanceof ShadowRoot && rootNode.host instanceof HTMLElement) {
          current = rootNode.host;
          crossedShadowBoundary = true;
        }
      }
    }

    return visibleRect;
  }

  private shouldClipByAncestor(element: HTMLElement): boolean {
    const style = window.getComputedStyle(element);
    const part = element.getAttribute('part') ?? '';
    if (part.includes('progress') || part.includes('region')) {
      return true;
    }
    return style.overflow !== 'visible'
      || style.overflowX !== 'visible'
      || style.overflowY !== 'visible';
  }

  private intersectRects(a: DOMRect, b: DOMRect): DOMRect | null {
    const left = Math.max(a.left, b.left);
    const top = Math.max(a.top, b.top);
    const right = Math.min(a.right, b.right);
    const bottom = Math.min(a.bottom, b.bottom);
    if (right <= left || bottom <= top) {
      return null;
    }
    return new DOMRect(left, top, right - left, bottom - top);
  }

  private getCanvasInkScore(canvas: HTMLCanvasElement): number {
    if (!canvas.width || !canvas.height) return 0;
    const probe = document.createElement('canvas');
    probe.width = 96;
    probe.height = 96;
    const probeContext = probe.getContext('2d');
    if (!probeContext) return 100;
    probeContext.drawImage(canvas, 0, 0, probe.width, probe.height);
    const imageData = probeContext.getImageData(0, 0, probe.width, probe.height).data;

    let coloredPixels = 0;
    for (let i = 0; i < imageData.length; i += 4) {
      const r = imageData[i];
      const g = imageData[i + 1];
      const b = imageData[i + 2];
      const a = imageData[i + 3];
      const isNearWhite = r > 250 && g > 250 && b > 250;
      if (a > 10 && !isNearWhite) {
        coloredPixels += 1;
      }
    }

    return coloredPixels;
  }

}
